'use client';

import {
  amplitudeSendEvent,
  GTM_NOTSET_STRING,
  getScreenName,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import { Button, Typography } from '@musinsa/mds';
import { useDialog } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { createLoginUrl, getBrandConfig, getPaths, getRestrictionMessage } from '../../common';
import { useNavigate } from '../../common/hooks/useNavigate';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';

interface JoinEligibilityButtonProps {
  className?: string;
  buttonText?: string;
  disabled?: boolean;
}

export function JoinEligibilityButton({
  className,
  buttonText = '큐레이터 가입 신청하기',
  disabled = false,
}: JoinEligibilityButtonProps) {
  const dialog = useDialog();
  const { navigate } = useNavigate();
  const { service } = useDeviceInfo();
  const brandConfig = getBrandConfig(service);

  const {
    data: eligibility,
    isPending,
    error,
  } = useQuery({
    ...curatorQueries.join.checkJoinEligibility({}),
    meta: {
      skipGlobalErrorHandler: true,
    },
  });

  const handleClick = useCallback(() => {
    if (error) {
      if (service === SERVICE.MUSINSA) {
        const loginUrl = `${process.env.NEXT_PUBLIC_MUSINSA_BASE_URL}/auth/login?referer=${
          typeof window !== 'undefined' ? window.location.href : ''
        }`;
        window.location.href = loginUrl;
      } else {
        const loginUrl = createLoginUrl(`${process.env.NEXT_PUBLIC_29CM_BASE_URL}/my-page`);
        window.location.href = loginUrl;
      }
      return;
    }

    if (disabled || isPending) return;

    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_signup',
          section_name: 'IntroCTASection',
          section_title: 'IntroCTASection',
          position: 5,
          button_id: 'join_eligibility',
          button_name: '큐레이터 가입 신청하기',
          stage_status: 'step1_success',
        },
      });
    }

    if (service === SERVICE.MUSINSA) {
      amplitudeSendEvent({
        event_name: 'click_button',
        from: getScreenName(),
        section_name: 'curator_fab',
        button_name: 'curator_join',
      });
      pushDataLayer('click_button', {
        section_name: 'curator_fab',
        section_index: '1',
        button_name: 'curator_join',
        index: GTM_NOTSET_STRING,
        brand_id: GTM_NOTSET_STRING,
      });
    }

    if (eligibility?.eligible) {
      navigate(getPaths(service).join());
      return;
    }

    const message = getRestrictionMessage(eligibility?.restrictionType ?? null, brandConfig);
    if (message) {
      dialog.open({
        title: message,
        confirmButtonText: '확인',
      });
    }
  }, [disabled, isPending, eligibility, brandConfig, dialog, navigate]);

  return (
    <Button
      variant="primary"
      onClick={handleClick}
      disabled={disabled || isPending}
      className={twMerge('w-full', className)}
    >
      <Typography variant="body14pxMed">{buttonText}</Typography>
    </Button>
  );
}
